import React, { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, Download, History, Loader2 } from "lucide-react";
import { requestFirmwareInstall, cancelFirmwareInstall } from "../../services/api";
import { Button } from "../ui/button";
import MarkdownBlock from "../common/ChangelogMarkdown";
import { Field, Tag } from "../profile/ProfileParts";
import { cn } from "../../lib/utils";

const parseVer = (v) => {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(String(v || "").trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};

// >0 when a is newer than b; unparseable versions sort as oldest.
export const cmpVer = (a, b) => {
  const va = parseVer(a) || [-1, -1, -1];
  const vb = parseVer(b) || [-1, -1, -1];
  return va[0] - vb[0] || va[1] - vb[1] || va[2] - vb[2];
};

export const isUpdateAvailable = (device, latest) =>
  !!latest && cmpVer(latest.version, device.firmware_version) > 0;

export const isInstallQueued = (device) => device.pending_config_type === "ota_update";

const formatSize = (bytes) =>
  bytes != null ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : "";

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

// Heap figures are KB-scale, not MB like a firmware image — formatSize would
// render every one of them as "0.0 MB".
const formatBytes = (bytes) =>
  bytes != null ? `${(bytes / 1024).toFixed(1)} KB` : "";

// A deferral reason is only useful with a time on it: "reported 14/08/2026"
// cannot distinguish a stale reason from one raised a minute ago.
const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "");

// The notes file is a CHANGELOG section whose "## [X.Y.Z] - date" heading
// duplicates what the row already shows — drop it for display.
const stripNotesHeading = (notes) =>
  String(notes || "")
    .replace(/^## \[[^\]]+\][^\n]*\n+/, "")
    .trim();

const releaseMeta = (release) =>
  [formatDate(release.published_at), formatSize(release.size_bytes)].filter(Boolean).join(" · ");

const Notice = ({ tone = "muted", children }) => (
  <p
    className={cn(
      "text-xs leading-snug",
      tone === "warn" ? "text-yellow-500/90" : tone === "error" ? "text-destructive" : "text-muted-foreground",
    )}
  >
    {children}
  </p>
);

/**
 * Firmware for one timer (#47), inside its sheet in Settings: whether it's
 * up to date, a remote install of the latest release (asked first, cancellable
 * while queued), and why a queued install hasn't started (#68). Installs
 * always use the latest release; older ones are in the release history, for
 * flashing over USB.
 */
const TimerFirmware = ({ device, latest, minRemoteVersion, online, onChanged, onHistory, releaseCount = 0 }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const queued = isInstallQueued(device);
  const updateAvailable = isUpdateAvailable(device, latest);
  // Whether this device's firmware can act on a queued remote install
  // (KhronosFirmware#51). null min = no released firmware supports it yet.
  const supported = !!minRemoteVersion && cmpVer(device.firmware_version, minRemoteVersion) >= 0;
  const needs = minRemoteVersion ? ` (needs v${minRemoteVersion} or newer)` : "";

  const install = async () => {
    setConfirming(false);
    setBusy(true);
    setError(null);
    try {
      await requestFirmwareInstall(device.id);
      setNotice(`Install of v${latest.version} queued.`);
      await onChanged?.();
    } catch (err) {
      setError(err.message || "Couldn't queue the install");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await cancelFirmwareInstall(device.id);
      setNotice("Install cancelled.");
      await onChanged?.();
    } catch (err) {
      setError(err.message || "Couldn't cancel the install");
    } finally {
      setBusy(false);
    }
  };

  const reason = device.ota_status?.reason && (
    <Notice tone="warn">
      {device.ota_status.label || device.ota_status.reason}
      {device.ota_status.reason === "low_memory" && device.ota_status.largest_internal != null && (
        <> ({formatBytes(device.ota_status.largest_internal)} largest free block)</>
      )}
      {device.ota_status.at && (
        <span className="text-muted-foreground"> · reported {formatDateTime(device.ota_status.at)}</span>
      )}
    </Notice>
  );

  let body;
  if (queued) {
    body = (
      <div className="grid gap-2.5 rounded-xl border border-yellow-500/35 bg-yellow-500/5 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold">Install queued</span>
          {latest && <Tag>v{latest.version}</Tag>}
        </div>
        <Notice>
          {online
            ? "The timer is online, so it should start within a minute."
            : "It applies when the timer next connects."}{" "}
          The timer restarts when it&rsquo;s done.
        </Notice>
        {!supported && (
          <Notice tone="warn">
            This timer&rsquo;s firmware can&rsquo;t act on remote installs{needs}, so the queued
            install waits until it can. To update now, run Settings &gt; NETWORK &gt; UPDATE on the
            timer.
          </Notice>
        )}
        {reason}
        <div>
          <Button type="button" variant="outline" size="sm" onClick={cancel} disabled={busy}>
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Cancel install
          </Button>
        </div>
      </div>
    );
  } else if (updateAvailable) {
    body = (
      <div className="grid gap-2.5 rounded-xl border border-yellow-500/35 bg-yellow-500/5 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-x-2">
          <span className="text-sm font-semibold">Version {latest.version} is ready</span>
          <span className="text-xs text-muted-foreground">{releaseMeta(latest)}</span>
        </div>
        {latest.notes ? (
          <div className="max-h-48 overflow-y-auto pr-1 text-sm">
            <MarkdownBlock content={stripNotesHeading(latest.notes)} />
          </div>
        ) : (
          <Notice>No release notes for this version.</Notice>
        )}
        {!supported && !confirming && (
          <Notice tone="warn">
            This timer&rsquo;s firmware can&rsquo;t act on remote installs{needs}. To update now, run
            Settings &gt; NETWORK &gt; UPDATE on the timer.
          </Notice>
        )}
        {reason}
        {confirming ? (
          <div className="grid gap-2.5 border-t border-yellow-500/25 pt-2.5">
            <p className="text-sm leading-snug">
              {supported ? (
                <>
                  <span className="font-semibold">Install v{latest.version} on {device.name}?</span>{" "}
                  The timer restarts when it&rsquo;s done. Don&rsquo;t start a session until it&rsquo;s back.
                </>
              ) : (
                <>
                  <span className="font-semibold">{device.name} is on v{device.firmware_version}</span>, which
                  can&rsquo;t act on remote installs{needs}. The install will only sit queued.
                  Queue it anyway?
                </>
              )}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Not now
              </Button>
              <Button type="button" size="sm" onClick={install} disabled={busy}>
                {supported ? "Install" : "Queue it"}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button type="button" size="sm" onClick={() => setConfirming(true)} disabled={busy}>
              {busy ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Download className="mr-1 h-3.5 w-3.5" />
              )}
              Install {latest.version}
            </Button>
          </div>
        )}
      </div>
    );
  } else {
    body = (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-3 text-sm">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        {latest ? "Up to date" : "No firmware published yet"}
      </div>
    );
  }

  return (
    <Field label="Firmware" aside={`v${device.firmware_version || "?"}`}>
      {body}
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice>{notice}</Notice>}
      {releaseCount > 0 && (
        <button
          type="button"
          onClick={onHistory}
          className="flex items-center gap-2 justify-self-start text-sm font-medium text-primary"
        >
          <History className="h-4 w-4" />
          Release history ({releaseCount})
        </button>
      )}
    </Field>
  );
};

/**
 * Every published version, newest first. Downloads are for flashing over USB;
 * a remote install always uses the latest.
 */
export const ReleaseHistory = ({ releases, devices = [] }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="grid gap-3">
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
        {releases.map((release) => {
          const expanded = open === release.version;
          const onADevice = devices.some((d) => d.firmware_version === release.version);
          return (
            <div key={release.version}>
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-3 py-2.5">
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : release.version)}
                  className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 text-left"
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-sm font-semibold">v{release.version}</span>
                      {release.latest && (
                        <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[11px] font-semibold text-green-500">
                          Latest
                        </span>
                      )}
                      {onADevice && (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                          On your timer
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{releaseMeta(release)}</span>
                  </span>
                  <ChevronDown
                    className={cn("h-4 w-4 text-muted-foreground transition-transform", expanded && "rotate-180")}
                  />
                </button>
                <Button variant="outline" size="sm" asChild>
                  <a href={release.url} download aria-label={`Download v${release.version}`}>
                    <Download className="h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
              {expanded && (
                <div className="px-3 pb-3 text-sm">
                  {release.notes ? (
                    <MarkdownBlock content={stripNotesHeading(release.notes)} />
                  ) : (
                    <Notice>No release notes for this version.</Notice>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Notice>
        Downloads are for flashing over USB. Installing from the timer&rsquo;s sheet always uses the
        latest version.
      </Notice>
    </div>
  );
};

export default TimerFirmware;
